import { useState, useEffect, useRef } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useShoppingList, type Difficulty } from './useShoppingList'
import type { ShoppingItem } from './useShoppingList'
import type { ShoppingListTranslations } from '../../i18n'
import { themes } from '../../api/client'
import { useTimer } from '../useTimer'
import GameSetupScreen from '../GameSetupScreen'
import SaveErrorBanner from '../SaveErrorBanner'
import { useMobile } from '../../hooks/useMobile'
import { useSessionMode } from '../../professional/SessionModeContext'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import GamePlayHeader from '../GamePlayHeader'

interface ShoppingListConfig { difficulty: Difficulty }

const THEME_SLUG    = 'ai-1776597037327'
const ACTIVITY_SLUG = 'shoppinglist'
const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

interface ShoppingListGameProps {
  onBack: () => void
}

type GameScreen = 'setup' | 'playing' | 'complete'

/* ── Root ──────────────────────────────────────────────────────── */
export default function ShoppingListGame({ onBack }: ShoppingListGameProps) {
  const { t, language } = useTranslation()
  const isMobile        = useMobile()
  const sessionMode     = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<ShoppingListConfig>('shoppinglist', !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [difficulty, setDifficulty] = useState<Difficulty>((sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy')
  const [items,      setItems]      = useState<ShoppingItem[]>([])
  const [loading,    setLoading]    = useState(true)

  // Load theme images on mount / language change
  useEffect(() => {
    let cancelled = false
    Promise.resolve()
      .then(() => themes.get(THEME_SLUG))
      .then((theme) => {
        if (cancelled) return
        const mapped: ShoppingItem[] = theme.images.map((img) => ({
          id:       img.id,
          word:     language === 'es' ? img.wordEs : img.wordEn,
          imageUrl: img.url ?? null,
          emoji:    '',
        }))
        setItems(mapped)
        setLoading(false)
      })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [language])

  const hook = useShoppingList(difficulty, items)
  const { gameComplete, moves, startTime, endTime, correctCount, totalNeeded } = hook.state
  const { resetGame } = hook

  // In session mode, useShoppingList initializes with items=[] (theme not yet loaded).
  // Once the async theme fetch completes, reset the game so it starts with real items.
  // Same pattern as Memory/WordSearch sessionThemeResetDone.
  const sessionItemsResetDone = useRef(false)
  useEffect(() => {
    if (!sessionMode && !restored) return
    if (loading) return
    if (items.length === 0) return
    if (sessionItemsResetDone.current) return
    sessionItemsResetDone.current = true
    resetGame()
  }, [loading, items, sessionMode, resetGame]) // eslint-disable-line react-hooks/exhaustive-deps

  const { sessionId } = useGameSession({
    activitySlug: ACTIVITY_SLUG,
    difficulty,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete:  gameComplete && totalNeeded > 0,
    activitySlug:  ACTIVITY_SLUG,
    difficulty,
    moves,
    startTime,
    endTime,
    pairsTotal:   totalNeeded,
    pairsMatched: correctCount,
    sessionId,
    onCompleted:  () => { clear(); setScreen('complete') },
  })

  const handleStart = () => {
    hook.resetGame()
    saveConfig({ difficulty })
    setScreen('playing')
  }

  const sl = t.shoppinglist

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh', fontSize: '1.375rem' }}>
        {sl.loading}
      </div>
    )
  }

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}
      {screen === 'setup' && (
        <SetupScreen
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
          isMobile={isMobile}
        />
      )}
      {screen === 'playing' && (
        <PlayScreen
          state={hook.state}
          onStartShopping={hook.startShopping}
          onToggle={hook.toggleOption}
          onCheck={hook.checkAnswers}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
          isMobile={isMobile}
        />
      )}
      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🛒"
          title={sl.complete.title}
          subtitle={sl.complete.subtitle.replace('{correct}', String(correctCount)).replace('{total}', String(totalNeeded))}
          stats={[
            { value: `${totalNeeded > 0 ? Math.round((correctCount / totalNeeded) * 100) : 0}%`, label: sl.complete.accuracyLabel },
            { value: formatGameTime(startTime, endTime), label: sl.complete.timeLabel },
            { value: sl.difficulty[difficulty].label, label: sl.complete.levelLabel },
          ]}
          playAgainLabel={sl.complete.playAgain}
          changeLevelLabel={sl.complete.changeLevel}
          backLabel={sl.backToDashboard}
          onPlayAgain={() => { hook.resetGame(); saveConfig({ difficulty }); setScreen('playing') }}
          onChangeLevel={() => { clear(); setScreen('setup') }}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ──────────────────────────────────────────────── */
function SetupScreen({ difficulty, onDifficultyChange, onStart, onBack, isMobile }: {
  difficulty:         Difficulty
  onDifficultyChange: (d: Difficulty) => void
  onStart:            () => void
  onBack:             () => void
  isMobile:           boolean
}) {
  const { t } = useTranslation()
  const sl    = t.shoppinglist

  return (
    <GameSetupScreen
      title={sl.title}
      subtitle={sl.subtitle}
      backLabel={sl.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map((id) => ({ id, ...sl.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={sl.chooseDifficultyTitle}
      startLabel={sl.startBtn}
      onStart={onStart}
    >
      {/* Preview illustration */}
      <div style={{ ...s.preview, flexDirection: isMobile ? 'column' : 'row' }}>
        <div style={s.previewList}>
          <span style={s.previewListTitle}>{sl.previewListTitle}</span>
          {['🍎', '🥛', '🍞'].map((e, i) => (
            <span key={i} style={s.previewItem}>{e} ···</span>
          ))}
        </div>
        <span style={s.previewArrow}>{isMobile ? '↓' : '→'}</span>
        <div style={s.previewCart}>
          <span style={s.previewCartIcon}>🛒</span>
          <span style={s.previewCartLabel}>{sl.previewCartTitle}</span>
        </div>
      </div>
    </GameSetupScreen>
  )
}

/* ── Play Screen ───────────────────────────────────────────────── */
function PlayScreen({ state, onStartShopping, onToggle, onCheck, onBack, isMobile }: {
  state:            ReturnType<typeof useShoppingList>['state']
  onStartShopping:  () => void
  onToggle:         (id: number) => void
  onCheck:          () => void
  onBack:           () => void
  isMobile:         boolean
}) {
  const { t } = useTranslation()
  const sl    = t.shoppinglist
  const elapsed = useTimer(state.startTime, state.endTime)

  const progress = state.phase === 'memorise'
    ? 0
    : state.selected.size / state.totalNeeded

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={sl.title}
        stats={[
          { value: state.selected.size, label: sl.selected },
          { value: elapsed,             label: sl.time },
        ]}
        progress={progress * 100}
        progressNow={state.selected.size}
        progressMax={state.totalNeeded}
        onBack={onBack}
        backLabel={sl.backToDashboard}
      />

      <main style={s.playMain}>
        {state.phase === 'memorise' && (
          <MemoriseView
            items={state.shoppingList}
            onReady={onStartShopping}
            sl={sl}
            isMobile={isMobile}
          />
        )}
        {state.phase === 'shopping' && (
          <ShoppingView
            state={state}
            onToggle={onToggle}
            onCheck={onCheck}
            sl={sl}
            isMobile={isMobile}
          />
        )}
      </main>
    </div>
  )
}

/* ── Memorise view ─────────────────────────────────────────────── */
function MemoriseView({ items, onReady, sl, isMobile }: {
  items:    ShoppingItem[]
  onReady:  () => void
  sl:       ShoppingListTranslations
  isMobile: boolean
}) {
  return (
    <div style={s.memoriseRoot}>
      <h2 style={s.memoriseTitle}>{sl.memoriseTitle}</h2>
      <p style={s.memoriseHint}>{sl.memoriseHint}</p>

      <div style={{ ...s.listCard, maxWidth: isMobile ? '100%' : 420 }}>
        {items.map((item, i) => (
          <div key={item.id} style={s.listRow}>
            <span style={s.listIndex}>{i + 1}.</span>
            <span style={s.listWord}>{item.word}</span>
          </div>
        ))}
      </div>

      <button onClick={onReady} style={s.readyBtn}>
        {sl.readyBtn}
      </button>
    </div>
  )
}

/* ── Shopping view ─────────────────────────────────────────────── */
function ShoppingView({ state, onToggle, onCheck, sl, isMobile }: {
  state:    ReturnType<typeof useShoppingList>['state']
  onToggle: (id: number) => void
  onCheck:  () => void
  sl:       ShoppingListTranslations
  isMobile: boolean
}) {
  return (
    <div style={s.shoppingRoot}>
      {/* Cart section: images of items that were in the list */}
      <section style={s.cartSection}>
        <h2 style={s.sectionTitle}>{sl.cartTitle}</h2>
        <div style={{ ...s.cartGrid, gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)' }}>
          {state.cartItems.map((item) => (
            <div key={item.id} style={s.cartItem}>
              {item.imageUrl
                ? <img src={item.imageUrl} alt={item.word} style={s.cartImage} />
                : <span style={s.cartEmoji}>{item.emoji || '🛒'}</span>
              }
            </div>
          ))}
        </div>
      </section>

      {/* Options section: pick the missing words */}
      <section style={s.optionsSection}>
        <h2 style={s.sectionTitle}>{sl.optionsTitle}</h2>
        <p style={s.instruction}>
          {sl.instructionPick.replace('{n}', String(state.totalNeeded))}
        </p>
        <div style={{ ...s.optionsGrid, gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)' }}>
          {state.options.map((item) => {
            const picked   = state.selected.has(item.id)
            const capReached = state.selected.size >= state.totalNeeded
            const disabled = !picked && capReached
            return (
              <button
                key={item.id}
                onClick={() => onToggle(item.id)}
                style={{
                  ...s.optionBtn,
                  ...(picked ? s.optionBtnActive : {}),
                  ...(disabled ? s.optionBtnDisabled : {}),
                }}
                aria-pressed={picked}
                disabled={disabled}
              >
                {item.word}
              </button>
            )
          })}
        </div>
      </section>

      {/* Check button — enabled only when exactly N items selected */}
      <button
        onClick={onCheck}
        style={{ ...s.checkBtn, opacity: state.selected.size < state.totalNeeded ? 0.5 : 1 }}
        disabled={state.selected.size < state.totalNeeded}
      >
        {sl.checkBtn}
      </button>
    </div>
  )
}

/* ── Styles ────────────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:       '100dvh',
    backgroundColor: '#FAFAF7',
  },

  // Play layout
  playRoot: {
    display:        'flex',
    flexDirection:  'column',
    minHeight:      '100dvh',
    backgroundColor:'#FAFAF7',
  },
  playMain: {
    flex:    1,
    padding: '16px 16px 32px',
  },

  // Memorise view
  memoriseRoot: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            20,
    paddingTop:     16,
  },
  memoriseTitle: {
    fontSize:   '1.625rem',
    fontWeight: 800,
    color:      '#1a1a1a',
    margin:     0,
    textAlign:  'center',
  },
  memoriseHint: {
    fontSize:   '1.125rem',
    color:      '#555',
    margin:     0,
    textAlign:  'center',
    fontStyle:  'italic',
    maxWidth:   400,
  },
  listCard: {
    backgroundColor: '#fff',
    borderRadius:    16,
    padding:         '20px 24px',
    boxShadow:       '0 2px 12px rgba(0,0,0,0.08)',
    width:           '100%',
    boxSizing:       'border-box',
  },
  listRow: {
    display:     'flex',
    alignItems:  'center',
    gap:         12,
    padding:     '10px 0',
    borderBottom:'1px solid #f0f0f0',
  },
  listIndex: {
    fontSize:   '1.125rem',
    fontWeight: 700,
    color:      '#52B788',
    minWidth:   24,
  },
  listWord: {
    fontSize:   '1.375rem',
    fontWeight: 600,
    color:      '#1a1a1a',
  },
  readyBtn: {
    minHeight:       56,
    padding:         '0 40px',
    fontSize:        '1.25rem',
    fontWeight:      700,
    backgroundColor: '#52B788',
    color:           '#fff',
    border:          'none',
    borderRadius:    28,
    cursor:          'pointer',
    marginTop:       8,
  },

  // Shopping view
  shoppingRoot: {
    display:       'flex',
    flexDirection: 'column',
    gap:           24,
  },
  cartSection: {},
  optionsSection: {},
  sectionTitle: {
    fontSize:   '1.25rem',
    fontWeight: 700,
    color:      '#1a1a1a',
    margin:     '0 0 12px',
  },
  cartGrid: {
    display:   'grid',
    gap:       12,
  },
  cartItem: {
    backgroundColor: '#fff',
    borderRadius:    12,
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    aspectRatio:     '1',
    overflow:        'hidden',
    boxShadow:       '0 2px 8px rgba(0,0,0,0.08)',
  },
  cartImage: {
    width:      '100%',
    height:     '100%',
    objectFit:  'cover',
  },
  cartEmoji: {
    fontSize: '2.25rem',
  },
  instruction: {
    fontSize:   '1.125rem',
    color:      '#555',
    fontStyle:  'italic',
    margin:     '0 0 12px',
  },
  optionsGrid: {
    display: 'grid',
    gap:     12,
  },
  optionBtn: {
    minHeight:       56,
    padding:         '12px 16px',
    fontSize:        '1.125rem',
    fontWeight:      600,
    backgroundColor: '#fff',
    color:           '#1a1a1a',
    border:          '2px solid #e0e0e0',
    borderRadius:    12,
    cursor:          'pointer',
    textAlign:       'center',
    transition:      'all 0.15s',
  },
  optionBtnActive: {
    backgroundColor: '#E8F5EE',
    borderColor:     '#52B788',
    color:           '#2D6A4F',
  },
  optionBtnDisabled: {
    opacity:    0.35,
    cursor:     'not-allowed',
  },
  checkBtn: {
    minHeight:       56,
    width:           '100%',
    fontSize:        '1.25rem',
    fontWeight:      700,
    backgroundColor: '#52B788',
    color:           '#fff',
    border:          'none',
    borderRadius:    28,
    cursor:          'pointer',
    marginTop:       8,
  },

  // Complete screen
  completeRoot: {
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    minHeight:       '100dvh',
    backgroundColor: '#FAFAF7',
    padding:         24,
    boxSizing:       'border-box',
  },
  completeCard: {
    backgroundColor: '#fff',
    borderRadius:    24,
    padding:         '40px 32px',
    maxWidth:        480,
    width:           '100%',
    textAlign:       'center',
    boxShadow:       '0 4px 24px rgba(0,0,0,0.10)',
  },
  completeEmoji: {
    fontSize:     '4rem',
    marginBottom: 12,
  },
  completeTitle: {
    fontSize:   '1.875rem',
    fontWeight: 800,
    color:      '#1a1a1a',
    margin:     '0 0 8px',
  },
  completeSubtitle: {
    fontSize:   '1.125rem',
    color:      '#555',
    margin:     '0 0 24px',
  },
  statsRow: {
    display:         'flex',
    gap:             16,
    justifyContent:  'center',
    marginBottom:    32,
  },
  statBox: {
    flex:            1,
    backgroundColor: '#F5F9F6',
    borderRadius:    12,
    padding:         '14px 8px',
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'center',
    gap:             4,
  },
  statVal: {
    fontSize:   '1.375rem',
    fontWeight: 800,
    color:      '#1a1a1a',
  },
  statLbl: {
    fontSize: '0.875rem',
    color:    '#888',
  },
  completeBtns: {
    display:       'flex',
    flexDirection: 'column',
    gap:           12,
  },
  primaryBtn: {
    minHeight:       52,
    fontSize:        '1.125rem',
    fontWeight:      700,
    backgroundColor: '#52B788',
    color:           '#fff',
    border:          'none',
    borderRadius:    26,
    cursor:          'pointer',
  },
  secondaryBtn: {
    minHeight:       52,
    fontSize:        '1.125rem',
    fontWeight:      600,
    backgroundColor: '#fff',
    color:           '#1a1a1a',
    border:          '2px solid #e0e0e0',
    borderRadius:    26,
    cursor:          'pointer',
  },
  ghostBtn: {
    minHeight:       48,
    fontSize:        '1rem',
    fontWeight:      500,
    backgroundColor: 'transparent',
    color:           '#888',
    border:          'none',
    cursor:          'pointer',
  },

  // Setup preview
  preview: {
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    gap:             16,
    backgroundColor: '#F5F9F6',
    borderRadius:    16,
    padding:         '20px 16px',
    margin:          '8px 0',
  },
  previewList: {
    display:        'flex',
    flexDirection:  'column',
    gap:            6,
    backgroundColor:'#fff',
    borderRadius:   12,
    padding:        '12px 16px',
    minWidth:       120,
  },
  previewListTitle: {
    fontSize:   '0.875rem',
    fontWeight: 700,
    color:      '#888',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  previewItem: {
    fontSize: '1.125rem',
    color:    '#333',
  },
  previewArrow: {
    fontSize:   '1.75rem',
    color:      '#52B788',
    fontWeight: 700,
  },
  previewCart: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            6,
  },
  previewCartIcon: {
    fontSize: '2.5rem',
  },
  previewCartLabel: {
    fontSize:   '0.875rem',
    color:      '#555',
    fontWeight: 600,
  },
}
